import { Injectable } from '@angular/core';
import { Action, State, StateContext } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { of } from 'rxjs';
import { tap } from 'rxjs/operators';

import { FootprintsDataService } from '../footprints.data-service';
import { defaultFootprintsState, IFootprintsState } from '../model';
import { FootprintsAction } from './footprints.action';

@State<IFootprintsState>({
  name: 'footprints',
  defaults: defaultFootprintsState,
})
@Injectable()
export class FootprintsState {
  constructor(private footprintsDataService: FootprintsDataService) {}

  @Action(FootprintsAction.LoadFootprintsDonations)
  loadDonations(
    ctx: StateContext<IFootprintsState>,
    { brand }: FootprintsAction.LoadFootprintsDonations
  ) {
    const state = ctx.getState();
    return state.donations.length > 0
      ? of(state.donations)
      : this.footprintsDataService
          .loadDonations(brand)
          .pipe(tap((donations) => ctx.patchState({ donations })));
  }

  /**
   * This action checks if the current state already has fundable projects.
   * When loading from api, it returns a random array of projects and if the user
   * refresh the page, it would load a different set of projects.
   * To avoid UX issues, we keep the same projects for current session.
   */
  @Action(FootprintsAction.LoadFundableProjects)
  loadFundableProjects(
    ctx: StateContext<IFootprintsState>,
    { partnerCode, countryCodes }: FootprintsAction.LoadFundableProjects
  ) {
    const state = ctx.getState();
    return state.fundableProjects.length > 0
      ? of(state.fundableProjects)
      : this.footprintsDataService
          .loadFundableProjects(partnerCode, countryCodes)
          .pipe(
            tap((fundableProjects) => ctx.patchState({ fundableProjects }))
          );
  }

  @Action(FootprintsAction.SetSelectedProject)
  setSelectedProject(
    ctx: StateContext<IFootprintsState>,
    { projectId }: FootprintsAction.SetSelectedProject
  ) {
    const projects = ctx.getState().fundableProjects;
    const newState = projects.map((p) => ({
      ...p,
      isSelected: projectId === p.projectId,
    }));

    ctx.setState(patch({ fundableProjects: newState }));
  }

  @Action(FootprintsAction.SetSelectedDonationAmount)
  setSelectedDonationAmount(
    ctx: StateContext<IFootprintsState>,
    { amount }: FootprintsAction.SetSelectedDonationAmount
  ) {
    const donations = ctx.getState().donations;
    const newState = donations.map((d) => ({
      ...d,
      isSelected: d.value === amount,
    }));

    ctx.setState(patch({ donations: newState }));
  }
}
